<!-- Generic card component with reference to Material's anatomy using scoped slots
  https://material.io/components/cards#anatomy
-->
<template>
  <div
    :id="anchor"
    ref="root"
    tabIndex="0"
    class="card-component"
    :class="classes"
    :style="cssVars"
    @click="onClick"
  >
    <!-- For multiple shadows that requires rotate transformation
      -- Cannot use pseudo-elements as multiple shadows wouldn't work and
      :before is reserved for the class border -->
    <slot name="shadow" />
    <slot name="tooltip" />
    <div v-if="hasLabelList" class="card-label-list">
      <slot name="labellist" />
    </div>
    <div v-if="hasLabel" class="card-label">
      <slot name="label" />
    </div>
    <div v-if="hasThumbnail" class="card-thumbnail">
      <slot name="thumbnail" />
    </div>
    <div v-if="hasHeader || hasMetadata" class="card-header" @click.stop="onHeaderClick">
      <slot name="header" />
      <div v-if="hasBackground" class="card-background">
        <slot name="background" />
      </div>
      <ul v-if="!loading && hasMetadata" class="metadata">
        <slot name="metadata" />
      </ul>
    </div>
    <div v-if="hasContent" ref="content" class="card-content">
      <slot name="content" />
      <div class="content-info">
        <slot name="content-info" />
      </div>
    </div>
    <div v-if="hasActions" class="card-actions">
      <slot name="actions" />
    </div>

    <div v-if="loading" class="card-loader">
      <cv-skeleton-text heading width="100%" />
      <cv-skeleton-text paragraph width="100%" />
    </div>
  </div>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils";
import ColorUtils from "@/utils/color.utils.ts";
import subjectColorMap from "@configs/subjectColorMap";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onBeforeUpdate,
  onMounted,
  ref,
  useAttrs,
  useSlots,
  watch,
} from "vue";

interface Props {
  loading?: boolean;
  size?: "2xsmall" | "xsmall" | "small" | "medium" | "large" | "xlarge" | "2xlarge" | " ";
  truncate?: boolean;
  subjectKey?: string;
  anchor?: string;
  mediaLoaded?: boolean;
  expanded?: boolean;
  scrollOnExpand?: boolean;
}

const {
  loading = false,
  size = "small",
  truncate = false,
  subjectKey = "",
  anchor = "",
  mediaLoaded = false,
  expanded = false,
  scrollOnExpand = false,
} = defineProps<Props>();

const emit = defineEmits<{
  onClick: [];
  onHeaderClick: [];
}>();

const slots = useSlots();
const attrs = useAttrs();

const headerIconsLoaded = ref(false);
const contentOverflow = ref(false);
const contentObserver = ref<MutationObserver | null>(null);

const content = ref<HTMLElement | null>(null);
const root = ref<HTMLElement | null>(null);

const hasLabelList = computed(() => !!slots.labellist);
const hasBackground = computed(() => !!slots.background);
const hasLabel = computed(() => !!slots.label);
const hasHeader = computed(() => !!slots.header);
const hasMetadata = computed(() => !!slots.metadata);
const hasThumbnail = computed(() => !!slots.thumbnail);
const hasContent = computed(() => !!slots.content);
const hasActions = computed(() => !!slots.actions);
const hasSubjectCodeColor = computed(() => {
  return subjectKey && subjectColorMap.getSubjectColor(subjectKey) !== "";
});
const hasHeaderClickListener = computed(() => {
  return attrs && attrs.onHeaderClick; // listener will removed once declared in emits option
});

const classes = computed(() => [
  {
    "_has-subject-code": hasSubjectCodeColor.value,
    "_header-icons-loaded": headerIconsLoaded.value,
    "_content-overflow": truncate && !expanded && contentOverflow.value,
  },
  `_${size}`,
]);

const cssVars = computed(() => {
  const subjectColor = subjectColorMap.getSubjectColor(subjectKey);
  return {
    "--color": subjectColor,
    "--tinted-color": ColorUtils.getTintedColor(subjectColor, 70),
  };
});

watch(
  () => mediaLoaded,
  (value) => {
    if (value) {
      checkContentOverflow();
    }
  },
);

watch(
  () => expanded,
  async (value) => {
    if (!value || !scrollOnExpand) return;
    // Allow the containing accordion to open before measuring visibility.
    await nextTick();
    root.value?.scrollIntoView({ block: "nearest", inline: "nearest" });
  },
  { immediate: true },
);

onMounted(async () => {
  contentObserver.value = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (truncate && mutation.type === "childList") {
        checkContentOverflow();
      }
    }
  });

  if (hasContent.value) {
    if (content.value) {
      contentObserver.value.observe(content.value, { childList: true, subtree: true });
    }
    await nextTick();
    if (truncate) {
      checkContentOverflow();
    }
  }
});

onBeforeUpdate(async () => {
  if (truncate) {
    await nextTick();
    checkContentOverflow();
  }
});

onBeforeUnmount(() => {
  contentObserver.value?.disconnect();
});

function onClick(e) {
  const clickWhiteList =
    ".card-component .card-header" +
    ",.card-component .card-content a" +
    ",.card-component .card-content button" +
    ",.card-component .card-content .media-component div.plyr" +
    ",.card-component .card-actions";

  if (!DOMUtils.parent(e.target, clickWhiteList)) {
    emit("onClick");
  }
}

function onHeaderClick() {
  if (hasHeaderClickListener.value) {
    emit("onHeaderClick");
  } else {
    emit("onClick");
  }
}

function checkContentOverflow() {
  if (!content.value) {
    return;
  }
  const contentHeight = content.value.clientHeight;
  const children = content.value.children;
  let totalChildrenHeight = 0;
  // To give space allowance for the bottom of the content
  const offset = 16;

  for (const child of children) {
    // Client height doesn't include margin
    // https://stackoverflow.com/a/23749355
    const styles = window.getComputedStyle(child);

    // Exclude element with absolute position since they don't account for the height
    if (!styles.position.includes("absolute")) {
      const margin = parseFloat(styles.marginTop) + parseFloat(styles.marginBottom);

      totalChildrenHeight += child.clientHeight + margin;
    }
  }

  if (truncate && !expanded && totalChildrenHeight > contentHeight - offset) {
    contentOverflow.value = true;
  } else {
    contentOverflow.value = false;
  }
}
</script>

<style lang="scss">
@use "@styles/abstracts/mixins" as *;
@use "@styles/layout/breakpoint" as *;
@use "@styles/layout/z-map" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.card-component {
  display: flex;
  flex-direction: column;
  position: relative;
  border: 1px solid $ui-03;
  border-radius: 10px;
  background-color: $ui-background;
  word-break: break-word;
  overflow: hidden;
  cursor: pointer;

  .content-heading {
    display: flex;
    justify-content: space-between;
    margin-bottom: $spacing-02;
    .label-component {
      margin: 0;
    }
  }

  .content-info {
    display: flex;
    flex-flow: column;
    row-gap: $spacing-02;
    position: relative;
    margin-top: $spacing-03;
    gap: $spacing-03;
    background-color: $ui-02;
    padding: $spacing-03;
    border-radius: 10px;

    &:not(:has(.section-text)) {
      display: flex;
      flex-flow: row-reverse;
      justify-content: space-between;
    }

    .section-text {
      @include carbon--type-style("helper-text-01", true);
      @include line-clamp(2);
      color: $text-02;
    }

    .last-accessed {
      @include carbon--type-style("label-02");
      @include line-clamp(1);
      color: $text-02;
      span {
        @include carbon--type-style("card-label-03");
      }
    }

    &:empty {
      display: none;
    }

    .label-component {
      margin: 0;
      span {
        @include carbon--type-style("card-label-01");
      }
    }
  }
  @include media("desktop") {
    // 0.0694% is used to fix subpixel rounding issue in Safari
    width: calc((100% - (#{$spacing-08} * 2)) / 3 - 0.0694%);

    .content-subpage & {
      // 0.0694% is used to fix subpixel rounding issue in Safari
      width: calc((100% - (#{$spacing-05} * 2)) / 3 - 0.0694%);
    }
  }

  &._content-overflow {
    &::after {
      display: block !important;
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      background-image: $primary-truncation;
      z-index: z-map("default");
      border-radius: 10px;
      pointer-events: none;
    }
  }

  &.moe {
    .card-label {
      background-color: $location-01;
    }
  }

  &.community {
    .card-label {
      background-color: $location-02;
    }
  }

  &._disabled:not(&._2xlarge) {
    position: relative;
    cursor: not-allowed;

    &:active {
      pointer-events: none;
    }

    > div {
      &.card-header {
        &:hover {
          background-color: $ui-01;
          border-color: transparent;

          & ~ .card-content {
            background-color: $ui-background;
          }
        }

        .bx--form {
          .field-set {
            &.title {
              span {
                color: $disabled-02;
              }
            }
          }
        }

        .label-component {
          span {
            background-color: $ui-02;
            color: $disabled-02;
          }
        }

        .title {
          color: $disabled-02;
        }

        .metadata {
          li {
            color: $disabled-02;
          }

          .sls-icon {
            [fill] {
              fill: $disabled-02;
            }

            [stroke] {
              stroke: $disabled-02;
            }
          }
        }
      }

      &.card-content {
        color: $disabled-02;

        .sls-icon {
          [fill] {
            fill: $disabled-02;
          }
        }

        &:hover {
          border-color: transparent;
        }

        .field-set {
          &.schedule {
            .field-value {
              color: $disabled-02;
            }
          }

          &.section-info {
            .sls-icon {
              path {
                stroke: $disabled-02;
                fill: $disabled-01;
              }
              //[TODO-Shin] To clean up in upcomig card revamp
              path {
                &[fill] {
                  stroke: unset;
                }
              }

              &[name="Section32"] {
                [fill="#2B63D9"] {
                  fill: #69788a;
                }
                [fill="#6B92E4"] {
                  fill: #96a1ad;
                }
                [fill="#DFE8F9"] {
                  fill: unset;
                }
              }
            }
          }

          &.type-progress {
            .bar {
              background-color: $disabled-02;
            }
          }
        }
      }
    }

    .card-actions {
      pointer-events: none;

      .sls-icon {
        [fill] {
          fill: $disabled-02;
        }
      }
    }
  }

  // Hover and active state only applicable when the card is not disabled
  &:not(._disabled) {
    &:active {
      box-shadow: $shadow-02;
    }
  }

  /* Large cards would be displayed vertically and not have horizontal scrolling, unless they are nested in a carousel
   * Examples: [AssignmentCard.vue, AppListCard.vue, SubgroupCard.vue]
   * Regex to search: "<card-component(.|\n)*large"
  */

  .card-actions {
    position: absolute;
    display: flex;
    top: 0.75rem;
    right: 1rem;
    border-radius: 100%;
    z-index: calc(#{z-map("default")} + 2);

    .bx--overflow-menu__trigger {
      width: inherit;
      height: inherit;
    }

    .tooltip-button {
      .bx--tooltip__trigger.bx--tooltip--a11y {
        padding: 0;
      }
    }
  }

  &._2xsmall {
    @include media("tablet-max") {
      height: 13.75rem;
      min-width: 7.25rem;
      flex: 0 0 7.25rem;
    }

    @include media("mobile-landscape", "desktop") {
      height: 4.5rem;
      max-height: 4.5rem;
      width: 18rem;

      &._content-overflow {
        &::after {
          visibility: hidden;
        }

        &.annotation-list-item {
          .card-content {
            &::before {
              visibility: hidden;
            }
          }
        }
      }
    }
  }

  &._xsmall {
    height: 118px;
    flex-flow: row;
    overflow-x: unset;

    &:hover {
      outline: 1px solid $interactive-01;
    }
    @include media("mobile") {
      width: 100%;
      flex: 0 0 auto;
      display: flex;
    }

    @include media("tablet") {
      width: calc((100% - 1rem) / 2);
      flex: 0 0 auto;
    }

    .card-header {
      margin-top: $spacing-07;
      flex: auto;

      .title {
        @include line-clamp(2);
        margin-bottom: $spacing-02;
      }
    }

    .card-thumbnail {
      min-width: 3.75rem;
      justify-content: flex-start;
      margin: 2.75rem $spacing-05 0 $spacing-05;

      img {
        width: 3.75rem;
        height: 3.75rem;
        border-radius: 10px;
      }

      .icon {
        height: 3.75rem;
        width: 3.75rem;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    .card-actions {
      top: 4px;
      right: 4px;
    }
  }

  &._small {
    height: 11.875rem;
    @include media("mobile") {
      width: 100%;
      flex: 0 0 auto;
    }

    @include media("tablet") {
      width: calc((100% - 1rem) / 2);
      flex: 0 0 auto;
    }

    .card-thumbnail {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 0 $spacing-07 0;
      height: 64px;
      min-height: 64px;

      > img {
        width: 5.62rem;
        height: 4.25rem;
        margin-top: $spacing-09;
      }

      > .placeholder-thumbnail {
        margin-top: $spacing-07;
        transform: scale(0.5);
      }
    }

    .card-actions {
      top: 4px;
      right: 4px;
    }
  }

  &._medium {
    height: 13.75rem;
    width: 18rem;
    min-width: 18rem;
    max-height: 13.75rem;
    padding: $spacing-05;
  }

  &._xlarge {
    height: 34.5rem;
    width: 22.8rem;

    .card-content {
      padding: 0 $spacing-05 $spacing-05 $spacing-05;
      .rich-text-content {
        @include carbon--type-style("body-short-01");
        line-height: 1.15;
      }
    }
  }

  &._2xlarge {
    position: relative;
    min-width: 16rem;
    border-radius: 16px;

    .carousel-item & {
      min-height: 248px;
    }
    @include media("mobile") {
      width: 100%;
    }

    &:hover {
      outline: 1px solid $interactive-01;
    }
    &._has-subject-code {
      &::before {
        display: none;
      }
    }

    &.paused {
      position: relative;
      color: $disabled-02 !important;

      .label-component {
        span {
          background-color: $disabled-01;
          color: $disabled-02;
        }
      }
      .metadata {
        li {
          > span {
            .sls-icon {
              [fill] {
                fill: $disabled-02;
              }

              &[name="Passcode16"] {
                [fill="#DFE8F9"] {
                  fill: $disabled-01;
                }
              }
            }
          }
        }
      }

      .section-lines {
        > * {
          color: $disabled-02;
        }
      }

      .card-actions {
        .bx--overflow-menu {
          background-color: $disabled-02;
          .sls-icon {
            [fill] {
              fill: $ui-01;
            }
          }
        }
      }

      cursor: not-allowed;
      &:hover {
        border: 1px solid $ui-03;
      }
      &:focus,
      &:active {
        pointer-events: none;
      }
      .card-content {
        > .cv-tooltip {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          cursor: not-allowed;

          &.bx--tooltip__trigger {
            &::before {
              bottom: 7.5rem;
            }
            .bx--assistive-text {
              bottom: 7.1875rem;
            }
          }
        }
      }
    }

    .card-header {
      position: relative;
      height: 112px;
      background: url("@images/placeholder/empty-states/ResourceThumbnailEmpty.svg");
    }
    .card-label-list,
    .card-header,
    .card-content {
      z-index: 1;
    }
    .card-header {
      display: flex;
      padding: 0;
      .label-component {
        margin: $spacing-03 $spacing-05;
        span {
          @include carbon--type-style("card-label-01");
          height: 18px;
          display: flex;
          align-items: center;
        }
      }
    }

    .card-content {
      background-color: $ui-01;
      padding: $spacing-05;
      height: 100%;
      flex: auto;
      > .metadata {
        margin-bottom: $spacing-03;
      }
      .content-title {
        @include carbon--type-style("card-title-01");
        @include line-clamp(2);
        height: 38px;
      }

      .label-component {
        height: 18px;
        span {
          @include carbon--type-style("card-label-01");
          display: flex;
          align-items: center;
        }
      }
    }

    .section-lines {
      display: flex;
      justify-content: space-between;
    }
    .metadata {
      display: flex;
      align-items: center;

      li {
        @include metadata-separator();
        > span {
          position: relative;

          .sls-icon {
            [fill] {
              fill: $interactive-01;
            }

            &[name="Passcode16"] {
              [fill="#DFE8F9"] {
                opacity: 0.2;
              }
            }
          }
        }
        .cv-tooltip {
          position: absolute;
          width: 100%;
          height: 100%;
        }
      }
    }
    .metadata-info-group {
      display: flex;
      justify-content: space-between;
    }

    .card-actions {
      .bx--overflow-menu {
        background-color: $ui-01;
        &:hover {
          background: $ui-03;
        }
      }
      .bx--btn {
        background-color: $ui-01;
        border-radius: 100%;
        &:hover {
          background: $ui-03;
        }
      }
    }
  }

  &.selected {
    border: 1px solid $interactive-04;
    box-shadow: $shadow-02;
  }
  &._has-subject-code {
    &::before {
      content: "";
      position: absolute;
      // Use fractional pixels to cater for different zoom levels by user
      // https://stackoverflow.com/a/9080685
      top: 0.75px;
      left: 3px;
      width: calc(100% - 6px);
      border-top: 4px solid var(--color);
      z-index: z-map("card-subject-banner");
      border-radius: 10px 10px 0 0;
    }
  }

  &._header-icons-loaded {
    .card-header {
      .icons-wrapper {
        visibility: visible;
      }
    }
  }

  .card-background {
    display: flex;
    position: absolute;
    inset: 0;
    overflow: hidden;
    z-index: 0;
    pointer-events: none;
    > img {
      width: 100%;
      object-fit: cover;
    }
  }
  .metadata {
    display: flex;
    align-items: center;
    flex-flow: wrap;

    li {
      @include carbon--type-style("label-01");
      position: relative;
      display: flex;
      align-items: center;
      color: $text-02;

      &:not(:last-of-type) {
        padding-right: 9px;

        &::after {
          content: "";
          position: absolute;
          height: 2px;
          width: 2px;
          top: 46%;
          right: 4px;
          border-radius: 50%;
          background-color: $text-02;
        }
      }

      .view-count {
        @include carbon--type-style("label-01");
        max-height: 15px;
        display: flex;
        align-items: center;
        color: $text-02;

        .sls-icon {
          margin-right: $spacing-02;
        }
      }

      .review-count {
        .sls-icon {
          margin-right: $spacing-02;
        }
      }

      &.subject {
        text-transform: capitalize;
      }
    }
  }

  .card-header {
    flex: 0 0 auto;
    padding: $spacing-05 $spacing-05 0 0;

    // To position the tooltip for actions specifically instead of adding
    // it globally as the latter would break for the tooltips in the app header
    .bx--tooltip__trigger {
      &.bx--tooltip--bottom {
        &.bx--tooltip--align-end {
          .bx--assistive-text {
            right: 1rem;
          }
        }
      }
    }

    .bx--overflow-menu {
      position: absolute;
      top: 0.5rem;
      right: 0.25rem;
      width: 40px;
      height: 40px;
      border-radius: 100%;
      z-index: z-map("default");

      &:focus :hover {
        background-color: $hover-ui;
      }

      .bx--overflow-menu__trigger {
        width: inherit;
        height: inherit;
      }

      svg {
        width: 24px;
        height: 24px;
      }
    }

    .timestamp {
      flex: 0 0 100%;
      color: $text-02;

      .label {
        @include carbon--type-style("card-label-02", true);
        margin-right: $spacing-02;
      }

      // Override generic type token
      time {
        @include carbon--type-style("card-label-01", true);
      }
    }

    .icons-wrapper {
      position: absolute;
      top: 1rem;
      right: 1rem;
      visibility: hidden;

      .sls-icon {
        &:not(:first-child) {
          margin-left: $spacing-03;
        }
      }
    }
  }

  .card-content {
    @include carbon--type-style("card-short-01", true);
    @include media-hover();

    .keywords {
      .bx--tag {
        @include carbon--type-style("card-short-01", true);
      }
    }

    p {
      @include carbon--type-style("card-short-01", true);

      // Override due to generic styling for cv-notification having a lower specificity
      &.bx--inline-notification__title {
        font-weight: 700;
      }
    }
  }

  .card-label {
    @include carbon--type-style("card-label-01");
    position: absolute;
    left: 1rem;
    top: 1rem;
    background-color: $itt-01;
    color: $ui-01;
    border-radius: 5px;
    padding: $spacing-01 $spacing-03;
    z-index: z-map("default");
    text-transform: uppercase;
  }

  .card-thumbnail {
    > img {
      width: 100%;
      max-height: 100%;
      object-fit: cover;
      border-radius: 10px;
    }
  }

  .label-component {
    span {
      cursor: pointer;
    }
  }
}
</style>
